import { Layer, Source } from 'react-map-gl';
import { getFillLayer, getLineLayer } from './Layers';
import type { MapProject, MapHome, MapHomeLocation } from '@habity/types';
import { MAPBOX_CONFIG } from '~/constants';
import { useMemo } from 'react';

const { CLUSTER_MAX_ZOOM, CLUSTER_RADIUS } = MAPBOX_CONFIG;

export const HOME_LAYER_ID = 'homes-layer';
export const HOME_CLUSTER_ID = 'homes-cluster';
const HOME_CLUSTER_COUNT_ID = 'homes-cluster-count';
const HOME_SOURCE_ID = 'homes-source';
const DEFAULT_HOME_COLOR = '#6b7280'; // gray (fallback)

export const PROJECT_LAYER_ID = 'projects-layer';
export const PROJECT_CLUSTER_ID = 'projects-cluster';
const PROJECT_CLUSTER_COUNT_ID = 'projects-cluster-count';
const PROJECT_SOURCE_ID = 'projects-source';

interface AreaSourcesAndLayersProps {
  polygons: GeoJSON.Polygon[] | null;
}

export function AreaSourcesAndLayers({ polygons }: AreaSourcesAndLayersProps) {
  if (!polygons || !polygons.length) return null;

  return (
    <>
      {polygons?.map((geometry, index) => (
        <Source
          id={`area-${index}`}
          key={`area-${index}`}
          type="geojson"
          data={{
            type: 'Feature',
            properties: {},
            geometry,
          }}
        >
          <Layer
            {...getFillLayer(`area-${index}-fill`, `area-${index}`, '#000000')}
            beforeId="z-0"
          />
          <Layer
            {...getLineLayer(
              `area-${index}-line`,
              `area-${index}`,
              '#000000',
              2
            )}
            beforeId="z-0"
          />
        </Source>
      ))}
    </>
  );
}

interface DrawnPolygonsSourcesAndLayersProps {
  drawnPolygons: GeoJSON.Polygon[] | null;
}

export function DrawnPolygonsSourcesAndLayers({
  drawnPolygons,
}: DrawnPolygonsSourcesAndLayersProps) {
  if (!drawnPolygons || !drawnPolygons.length) return null;

  return (
    <>
      {drawnPolygons?.map((geometry, index) => (
        <Source
          id={`drawn-${index}`}
          key={`drawn-${index}`}
          type="geojson"
          data={{
            type: 'Feature',
            properties: {},
            geometry,
          }}
        >
          <Layer
            {...getFillLayer(
              `drawn-${index}-fill`,
              `drawn-${index}`,
              '#000000'
            )}
            beforeId="z-1"
          />
          <Layer
            {...getLineLayer(
              `drawn-${index}-line`,
              `drawn-${index}`,
              '#000000',
              2
            )}
            beforeId="z-1"
          />
        </Source>
      ))}
    </>
  );
}

type AnyMapHome = MapHome | MapHomeLocation;

function isFlat(h: AnyMapHome): h is MapHomeLocation {
  return 'latitude' in h && h.latitude != null;
}

function getHomePoints(homes: AnyMapHome[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: homes
      .map((h) => {
        const lat = Number(isFlat(h) ? h.latitude : h.address?.latitude);
        const lng = Number(isFlat(h) ? h.longitude : h.address?.longitude);
        const color = isFlat(h) ? h.color_hex : h.status?.color_hex;
        const category = isFlat(h) ? h.category : h.address?.category;
        if (!lat || !lng) return null;
        return {
          type: 'Feature' as const,
          properties: {
            id: h.id,
            category: category ?? '',
            color: color || DEFAULT_HOME_COLOR,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [lng, lat],
          },
        };
      })
      .filter(Boolean) as GeoJSON.Feature[],
  };
}

interface HomePointsSourcesAndLayersProps {
  homes: AnyMapHome[] | null;
}

export function HomePointsSourcesAndLayers({
  homes,
}: HomePointsSourcesAndLayersProps) {
  const homePoints = useMemo(() => {
    if (!homes || !homes.length) return null;
    return getHomePoints(homes);
  }, [homes]);

  if (!homePoints) return null;

  return (
    <Source
      id={HOME_SOURCE_ID}
      type="geojson"
      data={homePoints}
      cluster={true}
      clusterMaxZoom={CLUSTER_MAX_ZOOM}
      clusterRadius={CLUSTER_RADIUS}
    >
      <Layer
        id={HOME_LAYER_ID}
        type="circle"
        source={HOME_SOURCE_ID}
        filter={['!', ['has', 'point_count']]}
        paint={{
          'circle-color': ['get', 'color'],
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }}
        beforeId="z-3"
      />
      <Layer
        id={HOME_CLUSTER_ID}
        type="circle"
        source={HOME_SOURCE_ID}
        filter={['has', 'point_count']}
        paint={{
          'circle-color': DEFAULT_HOME_COLOR,
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 50, 40],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }}
        beforeId="z-3"
      />
      <Layer
        id={HOME_CLUSTER_COUNT_ID}
        type="symbol"
        source={HOME_SOURCE_ID}
        filter={['has', 'point_count']}
        layout={{
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        }}
        paint={{
          'text-color': '#ffffff',
        }}
        beforeId="z-3"
      />
    </Source>
  );
}

const DEFAULT_PROJECT_COLOR = '#6366f1'; // indigo (fallback)

function getProjectPoints(projects: MapProject[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: projects
      .filter((p) => p.latitude && p.longitude)
      .map((project) => ({
        type: 'Feature' as const,
        properties: {
          id: project.id,
          name: project.name,
          price_min: project.price_min,
          price_max: project.price_max,
          total_homes: project.total_homes,
          color: project.status?.color_hex || DEFAULT_PROJECT_COLOR,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [project.longitude!, project.latitude!],
        },
      })),
  };
}

interface ProjectPointsSourcesAndLayersProps {
  projects: MapProject[] | null;
}

export function ProjectPointsSourcesAndLayers({
  projects,
}: ProjectPointsSourcesAndLayersProps) {
  if (!projects || !projects.length) return null;

  const projectPoints = getProjectPoints(projects);

  return (
    <Source
      id={PROJECT_SOURCE_ID}
      type="geojson"
      data={projectPoints}
      cluster={true}
      clusterMaxZoom={CLUSTER_MAX_ZOOM}
      clusterRadius={CLUSTER_RADIUS}
    >
      <Layer
        id={PROJECT_LAYER_ID}
        type="circle"
        source={PROJECT_SOURCE_ID}
        filter={['!', ['has', 'point_count']]}
        paint={{
          'circle-color': ['get', 'color'],
          'circle-radius': 10,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }}
        beforeId="z-3"
      />
      <Layer
        id={PROJECT_CLUSTER_ID}
        type="circle"
        source={PROJECT_SOURCE_ID}
        filter={['has', 'point_count']}
        paint={{
          'circle-color': DEFAULT_PROJECT_COLOR,
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 50, 40],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }}
        beforeId="z-3"
      />
      <Layer
        id={PROJECT_CLUSTER_COUNT_ID}
        type="symbol"
        source={PROJECT_SOURCE_ID}
        filter={['has', 'point_count']}
        layout={{
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        }}
        paint={{
          'text-color': '#ffffff',
        }}
        beforeId="z-3"
      />
    </Source>
  );
}
